import { useEffect, useRef, useState } from 'react';
import { useNotification } from '../Contexts/NotificationContext';
import { useToken } from '../Contexts/TokenContext';

function HeaderNotifications() {
    const { setNotification } = useNotification();
    const [dropdownOpen, setDropdownOpen] = useState(false);
    const [notifications, setNotifications] = useState([]);
    const [visibleNotifications, setVisibleNotifications] = useState([]);
    const [loading, setLoading] = useState(false);
    const notificationsPerPage = 5;
    const dropdownRef = useRef(null);

    const { tokenData } = useToken();

    const baseUrl = process.env.REACT_APP_API_URL;
    const accessToken = localStorage.getItem('access_token');

    useEffect(() => {
        if (tokenData.notificacoes) {
            setNotifications(tokenData.notificacoes);
            setVisibleNotifications(tokenData.notificacoes.slice(0, notificationsPerPage));
        }
    }, [tokenData]);

    useEffect(() => {
        const handleClickOutside = (event) => {
            if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
                setDropdownOpen(false);
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    useEffect(() => {
        if (!dropdownOpen) {
            const unread = visibleNotifications.filter((n) => !n.visualizado);

            if (unread.length > 0) {
                updateNotificationsAsRead(unread);
            }
        }
    }, [dropdownOpen]);

    const handleScroll = () => {
        if (!dropdownRef.current) return;

        const bottomReached = dropdownRef.current.scrollTop + dropdownRef.current.clientHeight >= dropdownRef.current.scrollHeight - 5;

        if (bottomReached && visibleNotifications.length < notifications.length) {
            loadMoreNotifications();
        }
    };

    const loadMoreNotifications = async () => {
        if (loading) return;

        setLoading(true);

        const start = visibleNotifications.length;
        const end = start + notificationsPerPage;

        const nextNotifications = notifications.slice(start, end);

        setVisibleNotifications((prev) => [...prev, ...nextNotifications]);

        await updateNotificationsAsRead(nextNotifications);

        setLoading(false);
    };

    const updateNotificationsAsRead = async (notificationsToUpdate) => {
        if (!notificationsToUpdate || notificationsToUpdate.length === 0) return;

        try {
            const response = await fetch(`${baseUrl}/painel/notificacoes/visualizar/`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    Authorization: `Bearer ${accessToken}`,
                },
                body: JSON.stringify({
                    notificacoes: notificationsToUpdate.map((n) => n.id),
                }),
            });

            if (response.ok) {
                setVisibleNotifications((prev) =>
                    prev.map((n) => (notificationsToUpdate.some((x) => x.id === n.id) ? { ...n, visualizado: true } : n))
                );

                setNotifications((prev) =>
                    prev.map((n) => (notificationsToUpdate.some((x) => x.id === n.id) ? { ...n, visualizado: true } : n))
                );
            } else {
                throw new Error('Erro ao marcar notificações como visualizadas');
            }
        } catch (error) {
            setNotification({
                type: 'error',
                message: error.message,
                show: true,
            });
        }
    };

    return (
        <ul className="flex items-center gap-2 2xsm:gap-4">
            <li className="relative" ref={dropdownRef}>
                <button
                    className="relative flex h-8 w-8 items-center justify-center border-stroke"
                    onClick={(e) => {
                        e.preventDefault();
                        setDropdownOpen((prev) => !prev);
                    }}
                >
                    {notifications.some((n) => !n.visualizado) && (
                        <span className="absolute -top-0 right-0 z-1 h-2 w-2 rounded-full bg-red-500">
                            <span className="absolute -z-1 block h-full w-full animate-ping rounded-full bg-red-500 opacity-75"></span>
                        </span>
                    )}

                    <svg
                        xmlns="http://www.w3.org/2000/svg"
                        width="18"
                        height="20"
                        viewBox="0 0 18 20"
                        className="fill-white opacity-50 transition-all hover:opacity-100"
                    >
                        <path
                            d="M20.5,14.586V11a7.006,7.006,0,0,0-5.145-6.742,2,2,0,0,0-3.71,0A7.007,7.007,0,0,0,6.5,11v3.586L4.793,16.293A1,1,0,0,0,4.5,17v2a1,1,0,0,0,1,1h16a1,1,0,0,0,1-1V17a1,1,0,0,0-.293-.707ZM20.5,18H6.5v-.586l1.707-1.707A1,1,0,0,0,8.5,15V11a5,5,0,0,1,10,0v4a1,1,0,0,0,.293.707L20.5,17.414Zm-7,5a2.98,2.98,0,0,0,2.818-2H10.682A2.98,2.98,0,0,0,13.5,23Z"
                            transform="translate(-4.5 -3)"
                        />
                    </svg>
                </button>

                {dropdownOpen && (
                    <div
                        className="absolute -right-5 sm:-right-27 mt-2 flex h-90 flex-col rounded-md border border-secondary bg-white shadow-default w-80 max-w-[60vw]"
                        onScroll={handleScroll}
                    >
                        <div className="px-4 py-3 bg-secondary">
                            <h5 className="text-sm font-medium text-primary">Notificações</h5>
                        </div>

                        <ul className="flex h-auto flex-col overflow-y-auto">
                            {visibleNotifications.length > 0 ? (
                                visibleNotifications.map((notification, index) => (
                                    <li key={index} className="relative">
                                        <a
                                            className="w-5/6 flex flex-col gap-2 border-t border-stroke px-4 py-3 hover:bg-gray-2"
                                            href={notification.link}
                                        >
                                            <p className="text-sm">
                                                <span className="text-black">{notification.titulo}</span> {notification.tipo}
                                            </p>

                                            <p className="text-xs">{notification.data}</p>
                                        </a>

                                        {!notification.visualizado && (
                                            <span className="absolute top-1/2 -translate-y-1/2 right-5 w-2.5 h-2.5 bg-green-500 rounded-full"></span>
                                        )}
                                    </li>
                                ))
                            ) : (
                                <p className="py-3 px-4 text-sm">Não existem notificações novas</p>
                            )}
                        </ul>

                        {loading && <div className="text-center py-2 text-sm">Carregando...</div>}
                    </div>
                )}
            </li>
        </ul>
    );
}

export default HeaderNotifications;
